import {
  Fragment,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  CircleAlert,
  CircleCheck,
  Download,
  FileSpreadsheet,
  Loader2,
  Plus,
  Upload,
  X,
} from "lucide-react";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeaderClose,
  DialogTitle,
} from "@nasam/ui";

import type { UseMutationResult } from "@tanstack/react-query";

import { BULK_STATUS_STYLES } from "./BulkRowsTable";
import { bulkFileProblem, filterBulkRows, type BulkFilter } from "./rows";
import type {
  BulkImportResult,
  BulkLimits,
  BulkPreview,
  BulkPreviewOutcome,
  BulkRowBase,
} from "./types";

/** Centred on a desktop; on a phone, a sheet up from the bottom edge. */
const DIALOG_LAYOUT =
  "flex flex-col gap-0 overflow-hidden rounded-lg border-0 p-0 max-md:start-0 max-md:top-auto max-md:bottom-0 max-md:max-w-none max-md:translate-x-0! max-md:translate-y-0! max-md:rounded-b-none max-md:rounded-t-2xl max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom";

/** Cancel and Back: a borderless secondary button. */
const QUIET_BUTTON =
  "border-0 bg-transparent shadow-none hover:bg-container-hover";

const STEPS = ["upload", "check", "preview", "confirm"] as const;

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const FILTERS: readonly BulkFilter[] = [
  "ALL",
  "VALID",
  "INVALID",
  "DUPLICATE",
];

const FILTER_STYLES: Record<BulkFilter, string> = {
  ALL: "border-badge-border-purple bg-badge-bg-light-purple text-badge-text-purple",
  ...BULK_STATUS_STYLES,
};

function filterCount(
  preview: BulkPreview<BulkRowBase>,
  filter: BulkFilter,
): number {
  switch (filter) {
    case "ALL":
      return preview.totalRecords;
    case "VALID":
      return preview.validRecords;
    case "INVALID":
      return preview.invalidRecords;
    case "DUPLICATE":
      return preview.duplicateRecords;
  }
}

function megabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(2)}MB`;
}

/** The purple band atop both dialogs: an icon, the title and a close. */
function BulkDialogHeader({
  icon,
  title,
  description,
  closeLabel,
  closeDisabled,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly description?: string;
  readonly closeLabel: string;
  readonly closeDisabled: boolean;
}) {
  return (
    <div className="flex min-h-16 shrink-0 items-center justify-between gap-3 bg-primary-button-background px-4 py-3 md:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-primary-button-background [&_svg]:size-5">
          {icon}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <DialogTitle className="text-body-l-b text-primary-button-text">
            {title}
          </DialogTitle>
          {description === undefined ? null : (
            <DialogDescription className="truncate text-body-caption-m text-primary-button-text/80">
              {description}
            </DialogDescription>
          )}
        </div>
      </div>
      <DialogHeaderClose disabled={closeDisabled} label={closeLabel} />
    </div>
  );
}

/** Upload, Check, Preview, Confirm: the steps done, the one on, the rest. */
function BulkStepper({
  copyKey,
  current,
}: {
  readonly copyKey: string;
  readonly current: number;
}) {
  const { t } = useTranslation("fleetMonitoring", {
    keyPrefix: `${copyKey}.steps`,
  });
  return (
    <ol aria-label={t("label")} className="flex">
      {STEPS.map((step, index) => {
        const done = index < current;
        const on = index === current;
        const last = index === STEPS.length - 1;
        return (
          <li
            key={step}
            aria-current={on ? "step" : undefined}
            className={cn("flex flex-col gap-1.5", !last && "flex-1")}
          >
            <div className="flex items-center">
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full border-2",
                  done || on
                    ? "border-primary-button-background"
                    : "border-neutral-200 bg-white",
                  done && "bg-primary-button-background",
                )}
              >
                {done ? (
                  <Check
                    className="size-2.5 text-primary-button-text"
                    strokeWidth={4}
                    aria-hidden="true"
                  />
                ) : on ? (
                  <span className="size-1.5 rounded-full bg-primary-button-background" />
                ) : null}
              </span>
              {last ? null : (
                <span
                  className={cn(
                    "h-0.5 flex-1",
                    done ? "bg-primary-button-background" : "bg-neutral-200",
                  )}
                />
              )}
            </div>
            <span
              className={cn(
                "text-body-caption-m whitespace-nowrap",
                done || on
                  ? "text-primary-button-background"
                  : "text-text-placeholder",
                on && "text-body-caption-bold",
              )}
            >
              {t(step)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function StepHeading({
  title,
  subtitle,
}: {
  readonly title: string;
  readonly subtitle: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-body-l-b text-text">{title}</h3>
      <p className="text-body-s-r text-text-placeholder">{subtitle}</p>
    </div>
  );
}

/** Confirm (the last step): how many will be created and how many records
 *  won't. */
function BulkConfirmDialog({
  copyKey,
  open,
  valid,
  failed,
  loading,
  onCancel,
  onConfirm,
}: {
  readonly copyKey: string;
  readonly open: boolean;
  readonly valid: number;
  readonly failed: number;
  readonly loading: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const { t } = useTranslation("fleetMonitoring", {
    keyPrefix: `${copyKey}.confirmDialog`,
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !loading) onCancel();
      }}
    >
      <DialogContent
        closeLabel={t("close")}
        showCloseButton={false}
        onEscapeKeyDown={(event) => {
          if (loading) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (loading) event.preventDefault();
        }}
        className={cn(DIALOG_LAYOUT, "md:max-w-106")}
      >
        <BulkDialogHeader
          icon={<CircleCheck aria-hidden="true" />}
          title={t("title")}
          closeLabel={t("close")}
          closeDisabled={loading}
        />
        {/* The message fills what the dialog's least height leaves, with
            the actions at the bottom, as in the app's other dialogs. */}
        <div className="flex flex-1 flex-col items-center justify-center gap-1 px-4 pt-6 pb-5 text-center">
          <p className="text-body-m-b text-text">{t("heading")}</p>
          <DialogDescription className="text-body-s-r text-text-placeholder">
            {/* Counted in each language's plural forms. */}
            {failed === 0
              ? t("create", { count: valid })
              : `${t("create", { count: valid })} ${t("skipped", { count: failed })}`}
          </DialogDescription>
        </div>
        <div className="flex gap-3 px-4 pb-4 rtl:flex-row-reverse">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            disabled={loading}
            onClick={onCancel}
            className={cn(QUIET_BUTTON, "flex-1")}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            size="lg"
            disabled={loading}
            aria-busy={loading}
            onClick={onConfirm}
            className="flex-1"
          >
            {loading ? (
              <Loader2 className="animate-spin" aria-hidden="true" />
            ) : null}
            {t("confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export interface BulkUploadDialogProps<Row extends BulkRowBase> {
  /** The feature's copy under `fleetMonitoring`, e.g. `pois.bulkUpload`. */
  readonly copyKey: string;
  /** The feature's own limits: its file type, size and record count. */
  readonly limits: BulkLimits;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly downloadTemplate: UseMutationResult<void, Error, void>;
  readonly check: UseMutationResult<BulkPreviewOutcome<Row>, Error, File>;
  readonly upload: UseMutationResult<BulkImportResult<Row>, Error, File>;
  readonly errorReport: UseMutationResult<void, Error, File>;
  /** The feature's table of rows; the Check step shows why a row is refused. */
  readonly renderRows: (
    rows: readonly Row[],
    showReason: boolean,
  ) => ReactNode;
  /** The sentence on the import's summary. */
  readonly resultMessage: (result: BulkImportResult<Row>) => string;
}

type Step = "upload" | "check" | "preview";

/**
 * Bulk Upload, for any feature that imports a sheet: pick a filled-in template (Upload file),
 * have every record checked by the API (Check), review the valid ones
 * (Preview), then confirm to import them (Confirm) and see what was created.
 * The same file goes with each request: the API keeps nothing in between, so
 * Cancel just closes. Mount it afresh (`key`) for each upload to start on the
 * first step.
 */
export function BulkUploadDialog<Row extends BulkRowBase>({
  copyKey,
  limits,
  open,
  onOpenChange,
  downloadTemplate,
  check,
  upload,
  errorReport,
  renderRows,
  resultMessage,
}: BulkUploadDialogProps<Row>) {
  const { t } = useTranslation("fleetMonitoring", { keyPrefix: copyKey });
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<BulkPreview<Row> | null>(null);
  const [filter, setFilter] = useState<BulkFilter>("ALL");
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<BulkImportResult<Row> | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const busy = check.isPending || upload.isPending;

  // Scenarios 13 and 14: the refused rows (invalid and duplicate) as an
  // Excel file to fix and upload again, from the same file.
  const errorReportButton = (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      disabled={errorReport.isPending}
      aria-busy={errorReport.isPending}
      onClick={() => {
        if (file !== null) errorReport.mutate(file);
      }}
      className="shrink-0"
    >
      {errorReport.isPending ? (
        <Loader2 className="animate-spin" aria-hidden="true" />
      ) : (
        <Download aria-hidden="true" />
      )}
      {t("errorReport.download")}
    </Button>
  );

  const close = () => {
    if (!busy) onOpenChange(false);
  };

  // Scenarios 15 and 18: refused before it's sent.
  const choose = (picked: File | undefined) => {
    if (picked === undefined) return;
    const problem = bulkFileProblem(picked, limits);
    setFile(problem === null ? picked : null);
    setError(problem === null ? null : t(`errors.${problem}`));
  };
  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    choose(event.target.files?.[0]);
    // Picking the same file again still counts as a change.
    event.target.value = "";
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    choose(event.dataTransfer.files[0]);
  };

  const runCheck = () => {
    if (file === null) {
      setError(t("errors.noFile"));
      return;
    }
    check.mutate(file, {
      onSuccess: (outcome) => {
        if (outcome.kind === "refused") {
          setError(outcome.message);
          return;
        }
        setError(null);
        setPreview(outcome.preview);
        setFilter("ALL");
        setStep("check");
      },
    });
  };

  // Confirm: on success the dialog shows the import's summary (Scenario 21);
  // the list and the map are refreshed behind it.
  const runImport = () => {
    if (file === null) return;
    upload.mutate(file, {
      onSuccess: (imported) => {
        setResult(imported);
        setConfirming(false);
      },
    });
  };

  const valid = preview?.validRecords ?? 0;
  const failed =
    (preview?.invalidRecords ?? 0) + (preview?.duplicateRecords ?? 0);
  const current = confirming || result !== null ? 3 : STEPS.indexOf(step);

  let body: ReactNode;
  let footerNote: string | null = null;
  let actions: ReactNode;

  if (result !== null) {
    const skipped = (status: Row["status"]) =>
      result.failedRows.filter((row) => row.status === status).length;
    const tiles = [
      {
        label: t("done.total"),
        value: result.totalRecords,
        className: "bg-badge-bg-light-purple text-badge-text-purple",
      },
      {
        label: t("done.created"),
        value: result.createdRecords,
        className: "bg-badge-bg-light-green text-badge-text-green",
      },
      {
        label: t("done.failed"),
        value: skipped("INVALID"),
        className: "bg-badge-bg-light-red text-badge-text-red",
      },
      {
        label: t("done.duplicate"),
        value: skipped("DUPLICATE"),
        className: "bg-badge-bg-light-yellow text-badge-text-yellow",
      },
    ];
    body = (
      <div
        role="status"
        className="flex flex-col items-center gap-4 rounded-lg bg-primary-button-background/8 px-4 py-6 text-center md:mx-6"
      >
        <span className="flex size-12 items-center justify-center rounded-lg bg-primary-button-background text-primary-button-text">
          <CircleCheck className="size-6" aria-hidden="true" />
        </span>
        <div className="flex flex-col gap-1">
          <h3 className="text-body-l-b text-text">{t("title")}</h3>
          <p className="text-body-s-r text-text-placeholder">
            {resultMessage(result)}
          </p>
        </div>
        <dl className="grid w-full max-w-lg grid-cols-2 gap-2 md:grid-cols-4">
          {tiles.map((tile) => (
            <div
              key={tile.label}
              className={cn(
                "flex flex-col-reverse items-center gap-2 rounded-lg px-3 py-4",
                tile.className,
              )}
            >
              <dt className="text-body-caption-m text-text-placeholder">
                {tile.label}
              </dt>
              <dd className="text-body-m-b">{tile.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    );
    actions = (
      <>
        {result.failedRecords > 0 ? errorReportButton : null}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="rounded-sm text-body-s-r text-text underline-offset-4 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
        >
          {t("done.showOnMap")}
        </button>
      </>
    );
  } else if (step === "upload" || preview === null) {
    body = (
      <>
        {error === null ? null : (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-lg bg-badge-bg-light-red px-3 py-2.5 text-body-s-b text-badge-text-red"
          >
            <CircleAlert
              className="mt-0.5 size-4 shrink-0 fill-badge-text-red text-badge-bg-light-red"
              aria-hidden="true"
            />
            {error}
          </p>
        )}
        <div className="overflow-hidden rounded-lg border border-container-default-border">
          <div className="flex items-start justify-between gap-3 border-b border-container-divider bg-container-subtle-background p-3 md:p-4">
            <ul className="flex list-disc flex-col gap-1 ps-4 text-body-caption-m text-text-placeholder">
              <li>
                {t("rules.format")}{" "}
                <bdi className="text-body-caption-bold text-text">
                  {limits.fileExtension}
                </bdi>
              </li>
              <li>
                {t("rules.size")}{" "}
                <bdi className="text-body-caption-bold text-text">
                  {`${String(limits.maxFileMb)}MB`}
                </bdi>
              </li>
              <li>
                {t("rules.records")}{" "}
                <bdi className="text-body-caption-bold text-text">
                  {limits.maxRecords}
                </bdi>
              </li>
            </ul>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={downloadTemplate.isPending}
              aria-busy={downloadTemplate.isPending}
              onClick={() => downloadTemplate.mutate()}
              className="shrink-0 md:h-10 md:px-4"
            >
              {downloadTemplate.isPending ? (
                <Loader2 className="animate-spin" aria-hidden="true" />
              ) : (
                <Download aria-hidden="true" />
              )}
              {t("downloadTemplate")}
            </Button>
          </div>
          <div className="p-4 md:px-10 md:py-8">
            <div className="rounded-md border border-container-default-border p-3 shadow-[0_2px_8px_rgb(0_0_0/0.06)] md:p-4">
              <div
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={cn(
                  "flex min-h-32 flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-neutral-200 bg-container-subtle-background px-4 py-6 transition-colors md:min-h-31",
                  dragging && "border-primary bg-badge-bg-light-purple",
                )}
              >
                {file === null ? (
                  // The accepted format and the size limit, as Figma lays
                  // them out.
                  <div
                    dir="ltr"
                    className="flex w-full max-w-sm items-center justify-between text-body-caption-m text-text-placeholder"
                  >
                    <span>
                      {limits.fileExtension.slice(1).toUpperCase()}
                    </span>
                    <span>{`${String(limits.maxFileMb)}MB`}</span>
                  </div>
                ) : (
                  <div className="flex w-full max-w-sm items-center gap-3 rounded-lg border border-container-default-border bg-white px-3 py-2">
                    <FileSpreadsheet
                      className="size-6 shrink-0 text-badge-text-green"
                      aria-hidden="true"
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <bdi className="truncate text-body-s-b text-text">
                        {file.name}
                      </bdi>
                      <bdi className="text-body-caption-m text-text-placeholder">
                        {megabytes(file.size)}
                      </bdi>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      disabled={check.isPending}
                      className="flex size-7 shrink-0 items-center justify-center rounded-md text-text-placeholder hover:bg-container-hover hover:text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:opacity-40"
                    >
                      <X className="size-4" aria-hidden="true" />
                      <span className="sr-only">{t("removeFile")}</span>
                    </button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={check.isPending}
                  onClick={() => input.current?.click()}
                >
                  <Plus aria-hidden="true" />
                  {t("browse")}
                </Button>
                <input
                  ref={input}
                  type="file"
                  accept={`${limits.fileExtension},${XLSX_MIME}`}
                  aria-label={t("fileInput")}
                  onChange={onInputChange}
                  className="sr-only"
                  tabIndex={-1}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
    actions = (
      <>
        <Button
          type="button"
          variant="secondary"
          disabled={check.isPending}
          onClick={close}
          className={cn(QUIET_BUTTON, "max-md:flex-1 md:min-w-28")}
        >
          {t("cancel")}
        </Button>
        <Button
          type="button"
          disabled={check.isPending}
          aria-busy={check.isPending}
          onClick={runCheck}
          className="max-md:flex-1 md:min-w-28"
        >
          {check.isPending ? (
            <Loader2 className="animate-spin" aria-hidden="true" />
          ) : null}
          {t("check")}
        </Button>
      </>
    );
  } else if (step === "check") {
    body = (
      <>
        <StepHeading
          title={t("results.title")}
          subtitle={t("results.subtitle", { file: file?.name ?? "" })}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div
            role="group"
            aria-label={t("results.filters")}
            className="flex flex-wrap gap-2"
          >
            {FILTERS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={filter === option}
                onClick={() => setFilter(option)}
                className={cn(
                  "inline-flex h-8 items-center gap-2 rounded-md border px-3 text-body-s-b transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
                  FILTER_STYLES[option],
                  filter !== option &&
                    "border-transparent opacity-80 hover:opacity-100",
                  filter === option &&
                    option === "ALL" &&
                    "min-w-20 justify-between",
                )}
              >
                {t(`status.${option}`)}
                <span className="flex size-4 items-center justify-center rounded-full border border-current text-[0.625rem] leading-none">
                  {filterCount(preview, option)}
                </span>
              </button>
            ))}
          </div>
          {failed > 0 ? errorReportButton : null}
        </div>
        {/* Keyed: another filter starts over on page 1. */}
        <Fragment key={filter}>
          {renderRows(filterBulkRows(preview.rows, filter), true)}
        </Fragment>
      </>
    );
    footerNote = t("results.summary", { valid, failed });
    actions = (
      <>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setStep("upload")}
          className={cn(QUIET_BUTTON, "max-md:flex-1 md:min-w-28")}
        >
          {t("back")}
        </Button>
        <Button
          type="button"
          disabled={valid === 0}
          onClick={() => setStep("preview")}
          className="max-md:flex-1 md:min-w-28"
        >
          {t("preview")}
        </Button>
      </>
    );
  } else {
    body = (
      <>
        <StepHeading
          title={t("review.title")}
          subtitle={t("review.subtitle")}
        />
        {renderRows(filterBulkRows(preview.rows, "VALID"), false)}
      </>
    );
    footerNote = t("review.note");
    actions = (
      <>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setStep("check")}
          className={cn(QUIET_BUTTON, "max-md:flex-1 md:min-w-28")}
        >
          {t("back")}
        </Button>
        <Button
          type="button"
          onClick={() => setConfirming(true)}
          className="max-md:flex-1 md:min-w-28"
        >
          {t("confirm")}
        </Button>
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
      >
        <DialogContent
          closeLabel={t("close")}
          showCloseButton={false}
          onEscapeKeyDown={(event) => {
            if (busy) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (busy) event.preventDefault();
          }}
          className={cn(DIALOG_LAYOUT, "max-h-[90dvh] md:max-w-210")}
        >
          <BulkDialogHeader
            icon={<Upload aria-hidden="true" />}
            title={t("title")}
            description={t("subtitle")}
            closeLabel={t("close")}
            closeDisabled={busy}
          />
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-5 pb-2 md:px-6 md:pt-6">
            <BulkStepper copyKey={copyKey} current={current} />
            {body}
          </div>
          <div className="flex shrink-0 flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:px-6">
            {footerNote === null ? null : (
              <p className="flex-1 text-body-caption-m text-text-placeholder">
                {footerNote}
              </p>
            )}
            {/* The summary's one link sits on the start side. */}
            <div className={cn("flex gap-3 rtl:flex-row-reverse", result === null && "md:ms-auto")}>
              {actions}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <BulkConfirmDialog
        copyKey={copyKey}
        open={confirming}
        valid={valid}
        failed={failed}
        loading={upload.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={runImport}
      />
    </>
  );
}
